import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, BadgeCheck, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { ProfileShell } from "../../components/profile/ProfileShell";

const EMAIL_DOMAINS = ["gmail.com", "yahoo.com", "outlook.com", "hotmail.com", "icloud.com", "proton.me"];
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

export default function EmailVerifyPage() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const alreadyVerified = !!user?.email_verified;
  const [step, setStep] = useState(user?.email ? "code" : "email");
  const [email, setEmail] = useState(user?.email || "");
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resendIn, setResendIn] = useState(0);

  // Live availability check + domain suggestions (Binance/Bybit-style)
  const [emailCheck, setEmailCheck] = useState({ status: "idle", reason: "" });
  const [suggestOpen, setSuggestOpen] = useState(false);
  const emailAbortRef = useRef(null);

  // Debounced availability check via /auth/check-email (the very same endpoint
  // the registration page uses). Skip when the address already belongs to the
  // current user (since "already taken" by themselves shouldn't look red).
  useEffect(() => {
    const raw = (email || "").trim().toLowerCase();
    const ownEmail = (user?.email || "").trim().toLowerCase();
    if (!raw || raw === ownEmail) {
      setEmailCheck({ status: "idle", reason: "" });
      return undefined;
    }
    setEmailCheck({ status: "checking", reason: "" });
    const t = setTimeout(async () => {
      try {
        if (emailAbortRef.current) emailAbortRef.current.abort();
        const ctrl = new AbortController();
        emailAbortRef.current = ctrl;
        const res = await fetch(
          `${API_BASE}/auth/check-email?email=${encodeURIComponent(raw)}`,
          { signal: ctrl.signal },
        );
        const data = await res.json();
        if (data && data.available) {
          setEmailCheck({ status: "available", reason: "" });
        } else {
          const r = (data && data.reason) || "Not available";
          const isTaken = /registered|taken|already/i.test(r);
          setEmailCheck({ status: isTaken ? "taken" : "invalid", reason: r });
        }
      } catch (e) {
        if (e && e.name === "AbortError") return;
        setEmailCheck({ status: "idle", reason: "" });
      }
    }, 420);
    return () => clearTimeout(t);
  }, [email, user?.email]);

  // Build the suggestion list — same logic as the register page
  const domainSuggestions = (() => {
    const raw = (email || "").trim();
    if (!raw) return [];
    const at = raw.indexOf("@");
    if (at < 0) return EMAIL_DOMAINS.map((d) => `${raw}@${d}`);
    const local = raw.slice(0, at);
    const partial = raw.slice(at + 1).toLowerCase();
    if (!local) return [];
    if (EMAIL_DOMAINS.includes(partial)) return [];
    return EMAIL_DOMAINS.filter((d) => d.startsWith(partial)).map((d) => `${local}@${d}`);
  })();

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn(resendIn - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const sendCode = async (e) => {
    e?.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email address");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/email/send-code", { email });
      // Dev mode: show the code as toast so the flow is testable end-to-end.
      if (data?.dev_code) {
        toast.success(`Code sent! (Dev: ${data.dev_code})`, { duration: 8000 });
      } else {
        toast.success("Verification code sent");
      }
      setStep("code");
      setResendIn(45);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send code");
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code || code.length < 4) {
      toast.error("Enter the 6-digit code");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/email/verify", { email, code });
      await refreshUser();
      toast.success("Email verified!");
      navigate("/profile/verification", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invalid code");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ProfileShell title="Email">
      {alreadyVerified ? (
        <div className="py-2 pb-10 flex flex-col items-center" data-testid="email-locked">
          <div className="pt-4 pb-6">
            <div
              className="h-32 w-32 rounded-2xl flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, rgba(25,165,92,0.18) 0%, rgba(25,165,92,0.04) 70%)",
                boxShadow: "inset 0 0 0 1px rgba(33,196,110,0.4), 0 12px 30px rgba(25,165,92,0.25)",
              }}
            >
              <BadgeCheck size={56} className="text-[#21c46e]" />
            </div>
          </div>
          <h2 className="text-2xl sm:text-[26px] font-extrabold text-white text-center">Email verified</h2>
          <p className="text-white/55 text-sm sm:text-[15px] text-center mt-2 max-w-sm leading-relaxed">
            Your email <b className="text-white">{user.email}</b> is verified and locked. For security, it cannot be changed.
          </p>
          <p className="text-white/45 text-xs text-center mt-3 max-w-sm">
            Need to update your email? Please contact support — they'll help after additional identity checks.
          </p>
          <button
            type="button"
            onClick={() => navigate("/profile/verification")}
            data-testid="back-to-verification"
            className="mt-6 h-11 px-5 rounded-lg bg-white/[0.06] border border-white/10 text-white/85 hover:bg-white/[0.10]"
          >
            Back to verification
          </button>
        </div>
      ) : (
      <div className="py-2 pb-10 flex flex-col items-center" data-testid="email-verify-page">
        {/* Hero illustration */}
        <div className="pt-4 pb-6 flex items-center justify-center">
          <div
            className="h-32 w-32 rounded-2xl flex items-center justify-center relative"
            style={{
              background:
                "linear-gradient(135deg, rgba(25,165,92,0.18) 0%, rgba(25,165,92,0.04) 70%)",
              boxShadow:
                "inset 0 0 0 1px rgba(33,196,110,0.25), 0 12px 30px rgba(25,165,92,0.25)",
            }}
          >
            <div
              className="h-20 w-24 rounded-md relative"
              style={{
                background:
                  "linear-gradient(180deg, #2fd57a 0%, #19a55c 60%, #0e6e3b 100%)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.35), 0 6px 14px rgba(25,165,92,0.5)",
              }}
            >
              {/* Letter peeking out */}
              <div
                className="absolute inset-x-3 top-2 bottom-6 rounded-sm"
                style={{
                  background: "linear-gradient(180deg,#fafafa 0%,#cfe9d8 100%)",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                }}
              >
                <div className="mt-3 mx-2 h-[2px] bg-[#19a55c]/40 rounded-full" />
                <div className="mt-2 mx-2 h-[2px] bg-[#19a55c]/30 rounded-full" />
                <div className="mt-2 mx-2 h-[2px] w-2/3 bg-[#19a55c]/30 rounded-full" />
              </div>
              {/* Envelope flap */}
              <div
                className="absolute inset-x-0 bottom-0 h-10"
                style={{
                  background: "linear-gradient(180deg, transparent 0%, #0e6e3b 100%)",
                  clipPath: "polygon(0 60%, 50% 100%, 100% 60%, 100% 100%, 0 100%)",
                }}
              />
            </div>
          </div>
        </div>

        <h2 className="text-2xl sm:text-[26px] font-extrabold text-white text-center">
          Email address
        </h2>
        <p className="text-white/55 text-sm sm:text-[15px] text-center mt-2 max-w-sm leading-relaxed">
          A verification code will be sent to the email address you provide.
        </p>

        <div className="w-full max-w-md mt-8 space-y-4">
          {step === "email" && (
            <form onSubmit={sendCode} className="space-y-4" data-testid="email-form">
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-white/55">
                  Email
                </span>
                <div className="relative">
                  <input
                    type="email"
                    data-testid="email-input"
                    value={email}
                    onChange={(e) => {
                      const v = (e.target.value || "").replace(/\s+/g, "").toLowerCase();
                      setEmail(v);
                      setSuggestOpen(true);
                    }}
                    onFocus={() => setSuggestOpen(true)}
                    onBlur={() => setTimeout(() => setSuggestOpen(false), 150)}
                    placeholder="Enter your email"
                    autoComplete="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    className={`mt-1 w-full h-12 rounded-lg bg-[#101824] px-4 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 lowercase border transition ${
                      emailCheck.status === "available"
                        ? "border-emerald-500/60 focus:border-emerald-500 focus:ring-emerald-500/25"
                        : emailCheck.status === "taken" || emailCheck.status === "invalid"
                          ? "border-rose-500/60 focus:border-rose-500 focus:ring-rose-500/25"
                          : emailCheck.status === "checking"
                            ? "border-amber-400/60 focus:border-amber-400 focus:ring-amber-400/25"
                            : "border-white/10 focus:border-[#19a55c] focus:ring-[#19a55c]/25"
                    }`}
                  />
                  {/* Domain suggestion dropdown — Binance/Bybit-style */}
                  {suggestOpen && domainSuggestions.length > 0 && (
                    <div
                      data-testid="email-suggestions"
                      className="absolute left-0 right-0 top-full mt-1 z-20 bg-[#0f1622] border border-white/10 rounded-lg shadow-xl overflow-hidden"
                    >
                      {domainSuggestions.slice(0, 5).map((s) => (
                        <button
                          key={s}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => {
                            setEmail(s);
                            setSuggestOpen(false);
                          }}
                          data-testid={`email-suggestion-${s.split('@')[1]}`}
                          className="w-full text-left px-3 py-2 text-[13px] text-white/80 hover:bg-[#19a55c]/15 hover:text-white transition flex items-center gap-2"
                        >
                          <Mail size={12} className="text-[#19a55c]/70" />
                          <span className="truncate">{s}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Live availability status pill */}
                {emailCheck.status !== "idle" && (
                  <div
                    data-testid="email-check-status"
                    className={`mt-1.5 flex items-center gap-1.5 text-[12px] font-semibold ${
                      emailCheck.status === "available"
                        ? "text-emerald-300"
                        : emailCheck.status === "taken" || emailCheck.status === "invalid"
                          ? "text-rose-300"
                          : "text-white/55"
                    }`}
                  >
                    {emailCheck.status === "checking" && <Loader2 size={13} className="animate-spin" />}
                    {emailCheck.status === "available" && <CheckCircle2 size={13} />}
                    {(emailCheck.status === "taken" || emailCheck.status === "invalid") && <AlertCircle size={13} />}
                    <span>
                      {emailCheck.status === "checking" && "Checking availability…"}
                      {emailCheck.status === "available" && "Available"}
                      {(emailCheck.status === "taken" || emailCheck.status === "invalid") && (emailCheck.reason || "Not available")}
                    </span>
                  </div>
                )}
              </label>
              <button
                type="submit"
                data-testid="email-send-code"
                disabled={
                  submitting ||
                  emailCheck.status === "taken" ||
                  emailCheck.status === "invalid"
                }
                className="w-full h-12 rounded-lg text-white font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background:
                    "linear-gradient(180deg,#21c46e 0%,#19a55c 55%,#12904e 100%)",
                  boxShadow:
                    "0 4px 14px rgba(25,165,92,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
                }}
              >
                {submitting ? "Sending…" : "Continue"}
              </button>
            </form>
          )}

          {step === "code" && (
            <form onSubmit={verifyCode} className="space-y-4" data-testid="code-form">
              <div className="flex items-center justify-center gap-2 text-white/65 text-sm">
                <Mail size={15} className="text-[#21c46e]" />
                <span className="truncate">{email}</span>
                <button
                  type="button"
                  onClick={() => setStep("email")}
                  className="text-[#21c46e] text-xs font-semibold hover:underline"
                >
                  Change
                </button>
              </div>
              <label className="block">
                <span className="text-xs uppercase tracking-wider text-white/55">
                  Verification code
                </span>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  data-testid="code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••••"
                  className="mt-1 w-full h-12 rounded-lg bg-[#101824] border border-white/10 px-4 text-white text-center text-xl font-bold tracking-[0.6em] placeholder:text-white/20 focus:border-[#19a55c] focus:outline-none focus:ring-2 focus:ring-[#19a55c]/25"
                />
              </label>
              <button
                type="submit"
                data-testid="code-verify"
                disabled={submitting}
                className="w-full h-12 rounded-lg text-white font-bold disabled:opacity-60"
                style={{
                  background:
                    "linear-gradient(180deg,#21c46e 0%,#19a55c 55%,#12904e 100%)",
                  boxShadow:
                    "0 4px 14px rgba(25,165,92,0.45), inset 0 1px 0 rgba(255,255,255,0.25)",
                }}
              >
                {submitting ? "Verifying…" : "Verify"}
              </button>
              <button
                type="button"
                disabled={resendIn > 0 || submitting}
                onClick={() => sendCode()}
                data-testid="code-resend"
                className="w-full h-10 text-white/55 hover:text-white text-sm disabled:opacity-50"
              >
                {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
              </button>
            </form>
          )}
        </div>
      </div>
      )}
    </ProfileShell>
  );
}
